import {
  emitNoteContentPasted,
  reportPasteFlavorDataFailure,
} from '@/components/tiptap/shared/emit-note-content-pasted';
import { isPasteSectionNormalizeKilled } from '@/features/flags/paste-section-normalize-flag';
import {
  restoreParagraphsFromBreakLines,
  simpleMarkdownToHtml,
} from '@/helpers/markdownToHtml';
import { type SessionCopySource } from '@/models/analytics/features/sessions';
import {
  classifyPasteFlavor,
  createDropTargetTracker,
  normalizePastedSections,
  pastedContentLength,
} from '@heidi/editor/core';
import { DOMParser } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Extension } from '@tiptap/react';
import { copySelectionToClipboard } from './copy-selection-to-clipboard';
import { sanitizePastedHtml } from './sanitize-pasted-html';

export type ClipboardTextSerializerOptions = {
  copySource: SessionCopySource;
};

export const ClipboardTextSerializer = Extension.create<ClipboardTextSerializerOptions>({
  name: 'clipboard-text-serializer',

  addOptions() {
    return {
      copySource: 'editor_clipboard',
    };
  },

  addProseMirrorPlugins() {
    const dropTarget = createDropTargetTracker();
    const copySource = this.options?.copySource ?? 'editor_clipboard';

    return [
      new Plugin({
        key: new PluginKey(this.name),

        props: {
          // Handle copy/cut events at ProseMirror level (before browser default)
          handleDOMEvents: {
            // Observe-only paste telemetry: one event per paste, classified
            // by clipboard flavor. Never handles the paste itself.
            paste: (_view, event) => {
              const data = event.clipboardData;
              if (data) {
                emitNoteContentPasted({
                  editorType: 'block_markdown',
                  pasteFlavor: classifyPasteFlavor(
                    data,
                    reportPasteFlavorDataFailure('block_markdown')
                  ),
                  contentLength: pastedContentLength(data),
                });
              }
              return false;
            },

            copy: (view, event) => {
              if (!copySelectionToClipboard(view, event, copySource)) {
                return false;
              }
              event.preventDefault();
              return true;
            },

            cut: (view, event) => {
              if (!copySelectionToClipboard(view, event, copySource)) {
                return false;
              }
              event.preventDefault();
              view.dispatch(view.state.tr.deleteSelection());
              return true;
            },

            drop: dropTarget.captureDrop,
          },

          // Strip inline styles and formatting from pasted HTML
          // This ensures pasted content doesn't bring unwanted colors/fonts.
          // The killswitch also decides whether `<h1>` survives — see below.
          transformPastedHTML(html) {
            // Restore marked paragraph-breaks before sanitizing so a note/section
            // copy round-trips as separate paragraphs, not one hard-break block.
            return sanitizePastedHtml(restoreParagraphsFromBreakLines(html), {
              keepSectionHeading: !isPasteSectionNormalizeKilled(),
            });
          },

          // Both paste paths meet here as a parsed `Slice`: the HTML flavor's
          // bare level-1 heading and the `sectionNode` `clipboardTextParser`
          // builds are reconciled into one structure. Gated by the same
          // killswitch as the sanitizer above, never separately — see the flag
          // module for why a half-flipped state is worse than either whole one.
          transformPasted(slice, view) {
            if (isPasteSectionNormalizeKilled()) return slice;
            return normalizePastedSections(slice, dropTarget.resolveContext(view));
          },

          clipboardTextParser(text, $context, plain, view) {
            text = simpleMarkdownToHtml({
              markdown: text,
            });

            // Create a temporary div to parse the HTML
            const dom = document.createElement('div');
            dom.innerHTML = text.trim();

            // Parse the HTML into a document fragment
            const slice = DOMParser.fromSchema(view.state.schema).parseSlice(dom, {
              preserveWhitespace: true,
              context: $context,
            });

            return slice;
          },
        },
      }),
    ];
  },
});
